import { GA4EventNames, WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { getVehicleInfoByLorryType } from "@vahak/core/dist/constants/lorryTypes";

import { getLoadQuoteCapacityStatus, LoadQuoteValues } from "../load-quote";
import { TrackEventHandler } from "../../../types/events";

type WebV2FieldScreen =
    | typeof WEB_V2_SOURCE_SCREENS.HOME_PAGE
    | typeof WEB_V2_SOURCE_SCREENS.LOAD_FORM
    | typeof WEB_V2_SOURCE_SCREENS.MARKETPLACE;

type TrackFieldOnceHandler = (dedupeKey: string, eventName: string, data?: Record<string, unknown>) => void;

function getLocationLabel(location?: string) {
    const trimmed = location?.trim();
    return trimmed || undefined;
}

export function trackWebV2LocationField(
    trackFieldOnce: TrackFieldOnceHandler,
    params: {
        field: "pickup" | "drop";
        sourceScreen: WebV2FieldScreen;
        location?: string;
    }
) {
    const location = getLocationLabel(params.location);
    if (!location) {
        return;
    }

    const eventName =
        params.field === "pickup" ? GA4EventNames.WEB_V2.pick_up_entered : GA4EventNames.WEB_V2.drop_entered;

    trackFieldOnce(params.field, eventName, {
        source_screen: params.sourceScreen,
        section: "location",
        location
    });
}

export function trackWebV2LorryTypeSelected(
    trackFieldOnce: TrackFieldOnceHandler,
    params: {
        sourceScreen: WebV2FieldScreen;
        lorryTypeId: number;
        lorryTypeLabel?: string;
        dedupeSuffix?: string | number;
    }
) {
    trackFieldOnce(`lorry-${params.dedupeSuffix ?? params.lorryTypeId}`, GA4EventNames.WEB_V2.lorry_type_selected, {
        source_screen: params.sourceScreen,
        section: "lorry type",
        lorry_type: params.lorryTypeLabel ?? String(params.lorryTypeId)
    });
}

export function trackLoadQuoteFormChange(
    trackFieldOnce: TrackFieldOnceHandler,
    patch: Partial<LoadQuoteValues>,
    context: {
        sourceScreen: typeof WEB_V2_SOURCE_SCREENS.HOME_PAGE | typeof WEB_V2_SOURCE_SCREENS.LOAD_FORM;
        isRemoteConfigLoaded: boolean;
        previousValues: LoadQuoteValues;
    }
) {
    const { sourceScreen, isRemoteConfigLoaded, previousValues } = context;

    if (patch.source && patch.source.place_id !== previousValues.source?.place_id) {
        trackWebV2LocationField(trackFieldOnce, {
            field: "pickup",
            sourceScreen,
            location: patch.source.description ?? patch.source.locality ?? patch.source.city
        });
    }

    if (patch.destination && patch.destination.place_id !== previousValues.destination?.place_id) {
        trackWebV2LocationField(trackFieldOnce, {
            field: "drop",
            sourceScreen,
            location: patch.destination.description ?? patch.destination.locality ?? patch.destination.city
        });
    }

    if (
        patch.tonnes != null &&
        patch.tonnes !== previousValues.tonnes &&
        getLoadQuoteCapacityStatus(patch.tonnes, isRemoteConfigLoaded) === "valid"
    ) {
        trackFieldOnce("capacity", GA4EventNames.WEB_V2.capacity_entered, {
            source_screen: sourceScreen,
            section: "capacity",
            capacity: patch.tonnes
        });
    }

    if (patch.lorryType != null && patch.lorryType !== previousValues.lorryType) {
        const lorryLabel = getVehicleInfoByLorryType(patch.lorryType, "load")?.label ?? String(patch.lorryType);
        trackWebV2LorryTypeSelected(trackFieldOnce, {
            sourceScreen,
            lorryTypeId: patch.lorryType,
            lorryTypeLabel: lorryLabel
        });
    }
}

export function trackLoadQuoteLocationInput(
    trackFieldOnce: TrackFieldOnceHandler,
    field: "pickup" | "drop",
    inputValue: string,
    sourceScreen: typeof WEB_V2_SOURCE_SCREENS.HOME_PAGE | typeof WEB_V2_SOURCE_SCREENS.LOAD_FORM
) {
    trackWebV2LocationField(trackFieldOnce, {
        field,
        sourceScreen,
        location: inputValue
    });
}
